import React, { useEffect, useState, useCallback } from 'react';
import {
  Loader2, Plus, Key, Copy, Check, Trash2, AlertCircle, ExternalLink,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { sdkKeysApi, ApiKey, CreatedApiKey } from '../../../services/sdkKeysApi';

interface DeveloperTabProps {
  clientId: string;
}

/**
 * "Developer" section in the marketplace — manages SDK API keys.
 *
 * Keys are per-client. Hand the secret to a partner dashboard once; we
 * only store the hash, so the secret can never be recovered. The list
 * shows prefix + last-used + name so coaches can identify which key is
 * still in use.
 */
export const DeveloperTab: React.FC<DeveloperTabProps> = ({ clientId }) => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [revealedKey, setRevealedKey] = useState<CreatedApiKey | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await sdkKeysApi.list(clientId);
      setKeys(items);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-100 p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
            <Key size={22} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-base">Developer / API Keys</h3>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              Create API keys to access your data programmatically — recipients, conversations,
              workflow stats, observations. Use these to power external dashboards, custom
              integrations, or BI tools.
            </p>
            <a
              href="/docs/sdk"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 font-medium mt-2"
            >
              View API documentation <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>

      {revealedKey && (
        <RevealedKeyBanner
          revealed={revealedKey}
          onDismiss={() => setRevealedKey(null)}
        />
      )}

      {/* List card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900">API keys</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {keys.length} active {keys.length === 1 ? 'key' : 'keys'}
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap"
          >
            <Plus size={15} /> Create key
          </button>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-gray-500">
            <Loader2 size={16} className="animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="px-5 py-6 text-sm text-red-700 bg-red-50">{error}</div>
        ) : keys.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-500">
            No keys yet. Create one to start using the API.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Prefix</th>
                <th className="px-5 py-3">Last used</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {keys.map((k) => (
                <KeyRow key={k.id} clientId={clientId} apiKey={k} onChanged={load} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateKeyModal
          clientId={clientId}
          onClose={() => setShowCreate(false)}
          onCreated={(k) => {
            setRevealedKey(k);
            setShowCreate(false);
            load();
          }}
        />
      )}
    </div>
  );
};

const KeyRow: React.FC<{
  clientId: string;
  apiKey: ApiKey;
  onChanged: () => void;
}> = ({ clientId, apiKey: k, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const revoke = async () => {
    if (!window.confirm(`Revoke "${k.name}"? Anything using this key will stop working immediately.`)) return;
    setBusy(true);
    try {
      await sdkKeysApi.revoke(clientId, k.id);
      toast.success('Key revoked');
      onChanged();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td className="px-5 py-3 text-gray-900 font-medium">{k.name}</td>
      <td className="px-5 py-3 font-sans text-xs text-gray-500">{k.key_prefix}…</td>
      <td className="px-5 py-3 text-xs text-gray-500">
        {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'Never'}
      </td>
      <td className="px-5 py-3 text-xs text-gray-500">
        {new Date(k.created_at).toLocaleDateString()}
      </td>
      <td className="px-5 py-3 text-right">
        <button
          onClick={revoke}
          disabled={busy}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 disabled:opacity-50"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          Revoke
        </button>
      </td>
    </tr>
  );
};

// -- create modal -----------------------------------------------------------

const CreateKeyModal: React.FC<{
  clientId: string;
  onClose: () => void;
  onCreated: (k: CreatedApiKey) => void;
}> = ({ clientId, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      const k = await sdkKeysApi.create(clientId, name.trim());
      onCreated(k);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to create key');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Create API Key</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Give it a memorable name — e.g. "Production dashboard" or "Local dev".
          </p>
        </div>
        <form onSubmit={submit} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Production dashboard"
              maxLength={128}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
              {submitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// -- revealed key banner ---------------------------------------------------

const RevealedKeyBanner: React.FC<{
  revealed: CreatedApiKey;
  onDismiss: () => void;
}> = ({ revealed, onDismiss }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(revealed.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — copy manually');
    }
  };
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 space-y-3">
      <div className="flex items-start gap-3">
        <AlertCircle size={20} className="text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-amber-900">
            Save this key now — it will not be shown again
          </h4>
          <p className="text-xs text-amber-800 mt-1 leading-snug">
            We only store the hash. If you lose this key, revoke it and create a new one.
          </p>
        </div>
      </div>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 px-3 py-2 rounded-lg bg-white border border-amber-300 text-xs font-sans text-gray-900 break-all">
          {revealed.key}
        </code>
        <button
          onClick={copy}
          className={`whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium ${
            copied ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white hover:bg-amber-700'
          }`}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <button
        onClick={onDismiss}
        className="text-xs font-medium text-amber-800 hover:text-amber-900"
      >
        I've saved it — dismiss
      </button>
    </div>
  );
};

export default DeveloperTab;
